/****************
 * THE BACKTESTER, SHARED BY THE WEB AND THE PHONE. 2026-10-20.
 *
 * Guillaume, 2026-10-20: Backtest on the phone, "synced with the web", a run
 * started on either shown on both, at 1:1 with the web's page. So the types,
 * the endpoints and every rule that turns a backtest into the Sessions page's
 * shapes live in ONE file, byte-identical in frontend/src/lib and
 * mobile-app/src/lib (scripts/sync-mobile-locales.mjs SHARED). Imports
 * nothing: each app hands makeBacktestApi its own fetch.
 *
 * Moved here from frontend/src/api/client.ts (types, endpoints) and
 * lib/backtestRows.ts (the rows), which now re-export it.
 ****************/

/* ── Types: the server's JSON (backend/internal/services/backtest) ─────── */

export type BacktestStatus = 'queued' | 'running' | 'done' | 'failed';

export interface BacktestSummary {
  starting_balance: number; end_balance: number; net_pnl: number; return_pct: number;
  gross_pnl: number; fees: number; funding: number; max_drawdown: number; max_drawdown_pct: number;
  trades: number; wins: number; losses: number; win_rate_pct: number; liquidations: number;
  best_trade: number; worst_trade: number; trader_orders: number; copied_orders: number;
  skipped_orders: number; kill_switch?: string;
  open_trades: number; unrealized_pnl: number; end_equity: number;
  // bt-6: the result's closed and open parts (closed + open = total), and the Sessions page's ratios.
  closed_net_pnl?: number; open_net_pnl?: number; total_pnl?: number; total_return_pct?: number;
  sharpe_ratio?: number; sortino_ratio?: number;
  breakeven?: number; long_trades?: number; short_trades?: number;
  close_reasons: Record<string, number>; skips_by_code: Record<string, number>;
  by_symbol: { symbol: string; trades: number; net_pnl: number; fees: number }[] | null;
}

export interface BacktestDataNotes {
  venue: string; minutes: number;
  substituted?: Record<string, number>; substituted_by?: Record<string, string>;
  funding_charges?: Record<string, number>; notes?: string[];
}

export interface Backtest {
  id: number; title: string; trader_uid: string; trader_name: string; venue: string;
  broker_filter?: string; period_start: string; period_end: string; starting_balance: number;
  strategy_name: string; strategy?: any; engine_version: string; parent_id?: number; session_id?: number; status: BacktestStatus;
  /** An AI session's backtest (2026-10-21): "replay" its recorded decisions, "reask" the user's own model. */
  ai_mode?: 'replay' | 'reask'; ai_context_spec?: string; ai_fallback?: string; ai_max_calls?: number;
  error?: string; progress: number; created_at: string; started_at?: string; finished_at?: string;
  summary?: BacktestSummary; curve?: { t: string; realised: number; equity: number }[];
  data_notes?: BacktestDataNotes; peak_heap_bytes: number; cpu_seconds: number; wall_seconds: number;
}

export interface BacktestTrade {
  id: number; symbol: string; side: 'LONG' | 'SHORT'; opened_at: string; closed_at?: string;
  entry_price: number; exit_price: number; max_size: number; leverage: number; max_margin: number;
  gross_pnl: number; fees: number; funding: number; net_pnl: number; close_reason: string; fills: number;
  close_reason_text?: string; open: boolean; initial_size: number; initial_margin: number;
  avg_entry_price: number; avg_exit_price: number; current_size: number; increases: number; decreases: number;
  /** Gross result without its increases and decreases (10-31); null when none. */
  plain_pnl_usd?: number | null;
  adjusted_gross_pnl_usd?: number | null;
  /** Its size changes by where the position stood (10-31); null when not read. */
  adjust_kinds?: Record<string, number> | null;
  /** What each kind of size change did, replayed without it (10-31). */
  adjust_effects?: Record<string, number> | null;
  mark_price?: number; unrealized_pnl?: number;
  /** MAE, % of margin (engine bt-3). */
  drawdown_pct?: number;
  /** bt-6: best point, % of margin, and the equity the trade opened against. */
  drawup_pct?: number;
  wallet_at_open?: number;
}

/** A session-shaped order row (replicated_orders' names). */
export interface BacktestOrder {
  id: number; trader_order_id?: number; created_at: string; symbol: string; master_action_type: string;
  direction: string; master_size_delta: number; adjusted_quantity: number; adjusted_leverage: number;
  execution_status: 'filled' | 'filtered'; execution_price: number; estimated_fee: number;
  filter_code?: string; filter_reason?: string; close_reason?: string; close_reason_text?: string;
}

export interface BacktestSkip {
  id: number; trader_order_id: number; at: string; symbol: string; action_type: string; code: string; reason: string;
}

export interface BacktestRequest {
  title?: string; trader_uid: string; trader_name?: string; from: string; to: string;
  starting_balance: number; strategy_name: string; strategy: any; broker_filter?: string;
  /** Re-ask: the AI decides; ai_max_calls is the estimate the user accepted. */
  ai_mode?: 'reask'; ai_context_spec?: string; ai_fallback?: string; ai_max_calls?: number;
}

/** What a re-ask will cost, before anything is called (POST /estimate). */
export interface ReaskEstimate {
  calls: number; exits: number; in_tokens_call: number; out_tokens_call: number;
  in_tokens: number; out_tokens: number; provider: string; model: string; note: string;
}

/** A trade's order as the trade view reads it (the web's InferredOrderResponse). */
export interface BacktestViewOrder {
  id: number; trader_uid: string; symbol: string; action_type: string; direction: string;
  execution_price: number; size_delta: number; size_after: number; leverage: number;
  nominal_value: number; margin_delta: number; estimated_fee: number; inferred_order_type: string;
  order_type_confidence: number; realized_pnl_delta: number; unrealized_pnl_after: number;
  inferred_at: string; created_at: string;
}

/* ── Endpoints. ADMIN_BACKTESTS: admins only (the server refuses others).
 * USER_BACKTESTS: every signed-in user, their own sessions only, when the
 * server has BACKTEST_PUBLIC=1 (2026-10-22, the Get Started flow); else 403. ── */

type Fetch = <T>(endpoint: string, options?: { method?: string; body?: string }) => Promise<T>;

export const ADMIN_BACKTESTS = '/api/v1/admin/backtests';
export const USER_BACKTESTS = '/api/v1/backtests';

/** One period from period-estimate: measured on the trader's last N days, cold (nothing cached). */
export interface PeriodEstimate {
  days: number;
  /** Within this caller's longest period. */
  allowed: boolean;
  orders: number;
  assets: number;
  asset_days: number;
  seconds_at_most: number;
}

export interface BacktestsEnabled {
  enabled: boolean;
  user_max_days: number;
  run_timeout_minutes: number;
  engine_version: string;
}

export function makeBacktestApi(fetchApi: Fetch, prefix: string = ADMIN_BACKTESTS) {
  const P = prefix;
  return {
  /** Whether this caller can backtest at all (no sign-in needed). */
  enabled: (): Promise<BacktestsEnabled> => fetchApi('/api/v1/backtests/enabled'),
  /** The caller's newest backtest, with queue_position while waiting. Rejects (404) when they have none. */
  latest: (): Promise<Backtest> => fetchApi(`${P}/latest`),
  /** How long a backtest of this trader takes over 7, 14 and 30 days. */
  periodEstimate: (traderUid: string): Promise<{ trader_uid: string; periods: PeriodEstimate[] }> =>
    fetchApi(`${P}/period-estimate?trader_uid=${encodeURIComponent(traderUid)}`),
  create: (req: BacktestRequest): Promise<Backtest> =>
    fetchApi(`${P}`, { method: 'POST', body: JSON.stringify(req) }),
  list: (): Promise<{ backtests: Backtest[] }> => fetchApi(`${P}?limit=200`),
  get: (id: number): Promise<Backtest> => fetchApi(`${P}/${id}`),
  trades: (id: number): Promise<{ trades: BacktestTrade[] }> => fetchApi(`${P}/${id}/trades`),
  skips: (id: number): Promise<{ skips: BacktestSkip[] }> => fetchApi(`${P}/${id}/skips`),
  orders: (id: number): Promise<{ orders: BacktestOrder[] }> => fetchApi(`${P}/${id}/orders`),
  compare: (ids: number[]): Promise<{ backtests: Backtest[] }> =>
    fetchApi(`${P}/compare?ids=${ids.join(',')}`),
  venues: (): Promise<{ venues: string[]; brokers: string[]; engine_version: string }> =>
    fetchApi(`${P}/venues`),
  /** A backtest of a session, every input taken from the session (admin only). */
  fromSession: (sessionId: number): Promise<Backtest> =>
    fetchApi(`${P}/from-session/${sessionId}`, { method: 'POST' }),
  /** What if, on a session: its unchanged baseline (reused when it exists) and a variant with this strategy. */
  whatIfSession: (sessionId: number, strategy: any): Promise<{ baseline: Backtest; variant: Backtest }> =>
    fetchApi(`${P}/from-session/${sessionId}`, { method: 'POST', body: JSON.stringify({ strategy }) }),
  /** A re-ask's cost: a create request, or an AI session (its own or these directives). */
  estimate: (req: BacktestRequest | { session_id: number; ai_context_spec?: string }): Promise<ReaskEstimate> =>
    fetchApi(`${P}/estimate`, { method: 'POST', body: JSON.stringify(req) }),
  /** An AI session: its replay and a re-ask of the caller's model (its own directives, or these). */
  reaskSession: (sessionId: number, aiContextSpec: string | undefined, maxCalls: number): Promise<{ baseline: Backtest; variant: Backtest }> =>
    fetchApi(`${P}/from-session/${sessionId}`, {
      method: 'POST', body: JSON.stringify({ ai_mode: 'reask', ai_context_spec: aiContextSpec, ai_max_calls: maxCalls }),
    }),
  /** A variant: the original's inputs, this strategy. The saved strategy is never touched. */
  rerun: (id: number, strategy: any, title?: string): Promise<Backtest> =>
    fetchApi(`${P}/${id}/rerun`, { method: 'POST', body: JSON.stringify({ strategy, title }) }),
  remove: (id: number): Promise<{ deleted: boolean }> =>
    fetchApi(`${P}/${id}`, { method: 'DELETE' }),
};
}

/* ── A backtest in the Sessions page's shapes ─────────────────────────── */
/*
 * A BACKTEST IN THE SESSIONS PAGE'S OWN SHAPES. 2026-10-15.
 *
 * Guillaume: the results must be presented "exactly the same as what we
 * present on Traders Page and Sessions Page". The tables are the session's own
 * components (components/session/SessionTradeTables.tsx) and the metrics box
 * is PerformanceSection; this file only renames a backtest's fields to the
 * names those read, so nothing on screen is computed a second way.
 *
 * 2026-10-16, Guillaume's review of his first result ("Lot of problems in the
 * data"): margin, PnL %, MAE and wallet size were missing; wins and losses
 * disagreed (the server counted a $0 trade as a loss, the box did not). Every
 * count now comes from the trades on the page, nothing from the summary that
 * could disagree with them.
 */


// store.PaperMaintenanceMarginRate: the backtest liquidates exactly as paper.
const PAPER_MAINTENANCE = 0.005;

/** Paper's isolated liquidation price (store/paper_liquidation.go). */
export function paperLiquidationPrice(side: string, entry: number, leverage: number): number {
  if (!(entry > 0) || !(leverage > 0)) return 0;
  const long = side.toUpperCase() !== 'SHORT';
  const liq = long ? entry * (1 - 1 / leverage + PAPER_MAINTENANCE) : entry * (1 + 1 / leverage - PAPER_MAINTENANCE);
  return liq > 0 ? liq : 0;
}

/**
 * A trade's margin, made as a session trade's margin_dollar is
 * (store/user_trade_assembler.go): average entry x LARGEST size / leverage.
 * 2026-10-19: this used the opening margin, so a trade that was increased
 * showed a bigger PnL% than the same trade on the Sessions page, and the
 * Return (net over margin) could not match. Never shown as 0.
 */
export function marginOf(t: BacktestTrade): number {
  const size = t.max_size || t.initial_size || 0;
  const entry = t.avg_entry_price || t.entry_price || 0;
  if (size > 0 && entry > 0 && t.leverage > 0) return (size * entry) / t.leverage;
  return t.max_margin || t.initial_margin || 0;
}

/** One backtest trade as a session trade row (open or closed). */
export function sessionTradeOf(t: BacktestTrade): any {
  const margin = marginOf(t);
  const net = t.net_pnl;
  const row: any = {
    id: t.id,
    symbol: t.symbol,
    direction: t.side,
    leverage: t.leverage,
    initial_size: t.initial_size || t.max_size,
    current_size: t.open ? t.current_size : 0,
    avg_entry_price: t.avg_entry_price || t.entry_price,
    avg_exit_price: t.avg_exit_price || t.exit_price,
    pnl_usd: t.open ? t.unrealized_pnl ?? 0 : t.gross_pnl,
    net_pnl_usd: net,
    pnl_percent: margin > 0 ? (net / margin) * 100 : 0,
    total_fees: t.fees,
    funding_paid: t.funding,
    order_count: t.fills,
    increase_count: t.increases,
    decrease_count: t.decreases,
    entry_time: t.opened_at,
    exit_time: t.closed_at,
    close_reason: t.close_reason,
    close_reason_text: t.close_reason_text,
    margin_dollar: margin,
    // MAE on margin, the trader page's unit (bt-3; absent before).
    drawdown_pct: t.drawdown_pct || 0,
    // bt-6: the best point on margin (the trade view's range bar) and the
    // equity it opened against ("size against the wallet"). Absent before.
    ...(t.drawup_pct ? { drawup_pct: t.drawup_pct } : {}),
    ...(t.wallet_at_open ? { wallet_at_open: t.wallet_at_open } : {}),
    // The Increases & Decreases Impact box (10-31): served by the backend, same rule as live.
    plain_pnl_usd: t.plain_pnl_usd ?? null,
    adjusted_gross_pnl_usd: t.adjusted_gross_pnl_usd ?? null,
    adjust_kinds: t.adjust_kinds ?? null,
    adjust_effects: t.adjust_effects ?? null,
    status: t.open ? 'OPEN' : 'CLOSED',
  };
  if (t.open) {
    row.mark_price = t.mark_price;
    row.liquidation_price = paperLiquidationPrice(t.side, row.avg_entry_price, t.leverage);
    row.liquidation_estimated = true;
    row.cross_isolated = 'Isolated';
  }
  return row;
}

/**
 * What only the backtest as a whole knows, in the session performance
 * endpoint's names. Counts, rates and MAE are NOT here: they are derived from
 * the trades on the page, so the box and the tables cannot disagree.
 */
export function sessionPerformanceOf(b: Backtest, trades: BacktestTrade[] | undefined): any {
  const s: BacktestSummary | undefined = b.summary;
  if (!s) return null;
  const biggest = (trades || []).reduce((m, t) => {
    const size = t.max_size || t.initial_size || 0;
    const entry = t.avg_entry_price || t.entry_price || 0;
    return Math.max(m, size * entry);
  }, 0);
  const net = (trades || []).filter((t) => t.open).reduce((a, t) => {
    const n = (t.current_size || 0) * (t.mark_price || t.avg_entry_price || 0);
    return a + (t.side === 'SHORT' ? -n : n);
  }, 0);
  return {
    trades_total_pnl_usd: s.net_pnl,
    // bt-6 and later; an older result has none, shown as not computed.
    ...(s.sharpe_ratio !== undefined ? { sharpe_ratio: s.sharpe_ratio, sortino_ratio: s.sortino_ratio } : {}),
    // The Sessions page's Return: closed trades' net over their margin
    // (handlers.go sessionROI), not the return on the starting balance.
    roi_percent: roiOnMargin(trades),
    // Session Return and Total PnL read realised + open against the starting
    // balance, as a session's do (margin_balance_is_live present = a session).
    starting_balance: b.starting_balance,
    margin_balance_is_live: false,
    // Worst trade as the server serves it for a session: -MIN(ROE), ROE =
    // gross on margin (handlers.go), not the net PnL% of the table.
    ...(() => {
      const roes = (trades || []).filter((t) => !t.open && marginOf(t) > 0).map((t) => (t.gross_pnl / marginOf(t)) * 100);
      return roes.length ? { worst_trade_percent: Math.max(0, -Math.min(...roes)) } : {};
    })(),
    total_fees_usd: s.fees,
    active_positions: s.open_trades ?? 0,
    unrealized_pnl: s.unrealized_pnl ?? 0,
    // The copy's account at the end: balance plus what is still open.
    margin_balance: s.end_equity ?? s.end_balance,
    biggest_position: biggest,
    net_position: net,
  };
}

/** A result made by an engine older than the one now running. */
export function madeByOlderEngine(b: Backtest, current: string | undefined): boolean {
  if (!current || !b.engine_version) return false;
  const v = (x: string) => Number(String(x).replace(/^bt-/, '')) || 0;
  return v(b.engine_version) < v(current);
}

/****************
 * THE RESULT WITH ITS OPEN TRADES. 2026-10-19.
 *
 * Guillaume: "it omits the PnL of Open Trades at the end, should be specified
 * that some trades are still open and an unrealised PnL is affecting results
 * and not just present the results of the closed Trades." Every place a
 * backtest's result is one number shows this total; the open part is named
 * beside it. bt-6 serves the split; an older result is rebuilt from what it
 * has (end equity - start = everything, net_pnl = what was realised).
 ****************/
export interface ResultParts {
  closed: number; // realised: closed trades, and fees/funding already paid on open ones (older results)
  open: number; // still open at the end, at the last price
  total: number;
  totalPct: number | null;
  openTrades: number;
}

export function resultOf(s: BacktestSummary | undefined | null): ResultParts | null {
  if (!s) return null;
  const total = s.total_pnl ?? ((s.end_equity ?? s.end_balance) - s.starting_balance);
  const closed = s.closed_net_pnl ?? s.net_pnl;
  return {
    closed,
    open: s.open_net_pnl ?? total - closed,
    total,
    totalPct: s.total_return_pct ?? (s.starting_balance > 0 ? (100 * total) / s.starting_balance : null),
    openTrades: s.open_trades ?? 0,
  };
}

/**
 * The Sessions page's Return (replicator/api/handlers.go: roi_net / roi_margin):
 * the closed trades' net over the sum of their margins, trades with a margin
 * only. undefined with none, never 0.
 */
export function roiOnMargin(trades: BacktestTrade[] | undefined): number | undefined {
  let net = 0, margin = 0;
  for (const t of trades || []) {
    if (t.open) continue;
    const m = marginOf(t);
    if (m > 0) { net += t.net_pnl; margin += m; }
  }
  return margin > 0 ? (100 * net) / margin : undefined;
}

/**
 * A backtest trade's own orders, as the trade view reads orders (2026-10-19).
 * The engine does not tie an order to a trade, so they are matched by asset,
 * side and time: the filled orders from the trade's open to its close. The
 * view is handed these and fetches nothing (a backtest trade's id is not a
 * trade on the server).
 */
export function ordersOfTrade(t: BacktestTrade, orders: BacktestOrder[] | undefined): BacktestViewOrder[] {
  const from = new Date(t.opened_at).getTime() - 60_000;
  const to = t.closed_at ? new Date(t.closed_at).getTime() + 60_000 : Infinity;
  return (orders || [])
    .filter((o) => o.execution_status === 'filled' && o.symbol === t.symbol
      && (!o.direction || o.direction.toUpperCase() === t.side.toUpperCase()))
    .filter((o) => { const ms = new Date(o.created_at).getTime(); return ms >= from && ms <= to; })
    .map((o) => {
      const lev = o.adjusted_leverage || t.leverage || 1;
      const nominal = o.execution_price * o.adjusted_quantity;
      return {
        id: o.id, trader_uid: '', symbol: o.symbol, action_type: o.master_action_type, direction: o.direction || t.side,
        execution_price: o.execution_price, size_delta: o.adjusted_quantity, size_after: 0, leverage: lev,
        nominal_value: nominal, margin_delta: nominal / lev, estimated_fee: o.estimated_fee,
        inferred_order_type: 'MARKET', order_type_confidence: 1, realized_pnl_delta: 0, unrealized_pnl_after: 0,
        inferred_at: o.created_at, created_at: o.created_at,
      };
    });
}

/**
 * "2026-10-01 14:30" or "2026-10-01" (UTC) as an ISO time, or '' when it is
 * not a real date and time. The phone's period boxes (2026-10-20): the web's
 * datetime-local input is UTC too (btx.new.utc).
 */
export function utcIsoOf(v: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?$/.exec(String(v || '').trim());
  if (!m) return '';
  const [y, mo, d, h, mi] = [+m[1], +m[2], +m[3], m[4] ? +m[4] : 0, m[5] ? +m[5] : 0];
  if (mo < 1 || mo > 12 || h > 23 || mi > 59) return '';
  const t = new Date(Date.UTC(y, mo - 1, d, h, mi));
  return t.getUTCDate() !== d ? '' : t.toISOString();
}

/****************
 * THE PERIOD IN THE PERSON'S OWN TIME (Guillaume 11-03: "The time selected by
 * default for the backtest is not good (it's in the future) make it 0h00 to
 * 0h00 and make sure to respect the time of the user").
 *
 * The period boxes hold LOCAL wall-clock time ("2026-11-03 00:00" is midnight
 * where the person is); localIsoOf turns that into the UTC instant the server
 * takes. The prefill used toISOString (UTC) inside these boxes, so at 21:00 in
 * Montreal "today" was already tomorrow, and the end sat in the future.
 ****************/

/** The Lite default: from local midnight `days` days ago to today's local midnight. */
export function liteBacktestPeriod(now: Date, days: number): { from: Date; to: Date } {
  let to = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // The server takes a period ending at least 2 minutes ago: in the first minutes
  // after midnight, end at the midnight before.
  if (now.getTime() - to.getTime() < 5 * 60_000) to = new Date(to.getFullYear(), to.getMonth(), to.getDate() - 1);
  const from = new Date(to.getFullYear(), to.getMonth(), to.getDate() - days);
  return { from, to };
}

/** A Date as the period box shows it, in local time: "YYYY-MM-DDTHH:mm" (web) or with a space (phone). */
export function localInputOf(d: Date, sep: 'T' | ' ' = 'T'): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}${sep}${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** "2026-10-01 14:30" / "2026-10-01T14:30" / "2026-10-01" in LOCAL time as a UTC ISO time, or '' when not a real date. */
export function localIsoOf(v: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?$/.exec(String(v || '').trim());
  if (!m) return '';
  const [y, mo, d, h, mi] = [+m[1], +m[2], +m[3], m[4] ? +m[4] : 0, m[5] ? +m[5] : 0];
  if (mo < 1 || mo > 12 || h > 23 || mi > 59) return '';
  const t = new Date(y, mo - 1, d, h, mi);
  return t.getDate() !== d ? '' : t.toISOString();
}

/* ── An AI's directives, inside its context spec (live's ContextSpec) ───── */

function specObject(spec: string | undefined | null): Record<string, unknown> {
  if (!spec) return {};
  try {
    const o = JSON.parse(spec);
    return o && typeof o === 'object' ? (o as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/** The directives an AI decides by (ContextSpec.instructions). */
export function directivesOf(spec: string | undefined | null): string {
  const v = specObject(spec).instructions;
  return typeof v === 'string' ? v : '';
}

/** The same spec with other directives; everything else in it kept. */
export function specWithDirectives(spec: string | undefined | null, directives: string): string {
  const o = specObject(spec);
  const d = directives.trim();
  if (d) o.instructions = d;
  else delete o.instructions;
  return JSON.stringify(o);
}
